import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api, API_BASE, getCookie} from '../api';

export default function Dashboard() {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef();
  const dragCounter = useRef(0);

  const load = () => api('/esign/documents').then(r => r.ok ? r.json() : []).then(setDocuments).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadFile = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name.replace(/\.[^.]+$/, ''));
    const jwtToken = getCookie('jwtToken');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    await fetch(`${API_BASE}/esign/documents`, { method: 'POST', headers, body: formData });
    if (fileRef.current) fileRef.current.value = '';
    setUploading(false);
    load();
  };

  const handleUpload = (e) => uploadFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    uploadFile(e.dataTransfer.files[0]);
  };

  return (
    <Layout>
      <div
        onDragEnter={e => { e.preventDefault(); dragCounter.current++; setDragging(true); }}
        onDragOver={e => e.preventDefault()}
        onDragLeave={e => { e.preventDefault(); dragCounter.current--; if (dragCounter.current === 0) setDragging(false); }}
        onDrop={handleDrop}
        className="relative"
      >
      {dragging && (
        <div className="absolute inset-0 bg-emerald-50 border-2 border-dashed border-emerald-400 rounded-lg z-10 flex items-center justify-center">
          <p className="text-emerald-600 text-lg font-medium">Drop PDF here to upload</p>
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">My Documents</h2>
        <label className={`px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          {uploading ? 'Uploading...' : 'Upload Document'}
          <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Signers</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {documents.map(doc => (
              <tr key={doc.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <Link to={`/documents/${doc.id}`} className="text-emerald-600 hover:text-emerald-800 font-medium">{doc.title}</Link>
                </td>
                <td className="px-6 py-4 hidden sm:table-cell">
                  <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                    doc.status === 'COMPLETED' ? 'bg-green-50 text-green-700' :
                    doc.status === 'SENT' ? 'bg-blue-50 text-blue-700' :
                    doc.status === 'VOIDED' ? 'bg-red-50 text-red-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{doc.status}</span>
                </td>
                <td className="px-6 py-4 text-gray-600 hidden sm:table-cell">{doc.signedCount}/{doc.signerCount}</td>
                <td className="px-6 py-4 text-gray-600">{doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {documents.length === 0 && <div className="text-center py-12 text-gray-500">No documents yet. Upload a PDF to get started.</div>}
      </div>
      </div>
    </Layout>
  );
}
